import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach current company id + active store id from localStorage for scoped endpoints
api.interceptors.request.use((config) => {
  const cid = localStorage.getItem("vetrich_company_id");
  if (cid) config.headers["X-Company-Id"] = cid;
  const sid = localStorage.getItem("vetrich_store_id");
  if (sid) config.headers["X-Store-Id"] = sid;
  return config;
});

// ---- Global error handler ----
// Silences duplicate toasts (same message in a 3s window) and surfaces
// friendly messages for 401 / 403 / 5xx even if the calling code forgets to.
// Callers that want to suppress auto-toast can set `config.silent = true`
// (used for polling endpoints / /auth/me on boot).
let _lastToast = { msg: "", at: 0 };
function _toastDedupe(msg, kind = "error") {
  if (!msg) return;
  const now = Date.now();
  if (msg === _lastToast.msg && now - _lastToast.at < 3000) return;
  _lastToast = { msg, at: now };
  (toast[kind] || toast.error)(msg);
}

let _redirectingToLogin = false;
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const path = (cfg.url || "").toString();
    const isAuthMe = path.endsWith("/auth/me") || path.endsWith("/auth/login");

    if (cfg.silent || isAuthMe) return Promise.reject(err);

    if (status === 401) {
      _toastDedupe("Your session has expired. Please sign in again.");
      if (!_redirectingToLogin && !window.location.pathname.startsWith("/login")) {
        _redirectingToLogin = true;
        // Give the toast a beat to render before we navigate away
        setTimeout(() => {
          window.location.assign("/login");
        }, 900);
      }
    } else if (status === 403) {
      const detail = formatApiError(err);
      _toastDedupe(detail || "You don't have permission to perform this action.");
    } else if (status >= 500) {
      _toastDedupe("Server error. Please try again in a moment.");
    } else if (status === 404) {
      // Only auto-toast if caller didn't specifically opt-out
      // (many pages legitimately query optional resources)
      // — no toast here, callers usually handle it inline.
    } else if (!status && err?.message) {
      // Network / CORS failure
      _toastDedupe("Network error — check your connection and retry.");
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const inr = (n) =>
  "₹" + (Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Format any date-ish value (Date | ISO string | YYYY-MM-DD | datetime-local) → DD-MM-YYYY.
 * Empty / invalid inputs return the fallback ("—" by default) so tables stay clean.
 */
export const fmtDate = (v, fallback = "—") => {
  if (!v) return fallback;
  // Fast path for YYYY-MM-DD to avoid TZ shifts
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const [y, m, d] = v.slice(0, 10).split("-");
    return `${d}-${m}-${y}`;
  }
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return fallback;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

/** Same as fmtDate but appends HH:MM (24h) — used for deadlines, updated_at, log timestamps. */
export const fmtDateTime = (v, fallback = "—") => {
  if (!v) return fallback;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return typeof v === "string" ? v : fallback;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
};
